// The session tick (2026-10-09): one 30-second poll per tab, shared.
//
// Before this, every open tab ran its own timers: my parks every 30 s,
// assignments, intent priorities and the automation-failure count every
// 60 s, every park too for an admin. About 40,000 small requests a day
// across the team, each taking a pool connection on the server. One request
// every 30 s, GET /api/session/tick, replaces them all; each consumer
// subscribes here and reads its own section of the result.
//
// Rules:
// - the timer runs only while someone is subscribed, and stops with the
//   last unsubscribe, so a signed-out shell polls nothing;
// - a new subscriber gets the last result at once when it is fresh (under
//   one interval old) and a fetch is started either way, so a mount still
//   refreshes like the old per-timer `tick()` on mount did;
// - refresh() is the imperative "now", for the moment after a park, a
//   release or an admin changing a priority level; a fetch already in flight
//   is reused, never doubled;
// - a hidden tab keeps its timer (the old timers did too), and a tab that
//   becomes visible refreshes at once so a sleeping laptop catches up;
// - a failed fetch is nobody's error: the previous result stands and the
//   next tick tries again. Consumers never see a rejection.
//
// createSessionTickBus() is the factory the tests drive with a fake fetch and
// clock; App.jsx creates the one real bus with api.js's fetchSessionTick.

export const SESSION_TICK_INTERVAL_MS = 30_000

export function createSessionTickBus({ fetch, intervalMs = SESSION_TICK_INTERVAL_MS, now = () => Date.now(), setInterval: setIntervalFn = globalThis.setInterval, clearInterval: clearIntervalFn = globalThis.clearInterval } = {}) {
  if (typeof fetch !== 'function') throw new Error('createSessionTickBus needs a fetch function')
  const listeners = new Set()
  let timer = null
  let last = null
  let lastAt = 0
  let inFlight = null

  function deliver(tick) {
    for (const fn of listeners) {
      try { fn(tick) } catch { /* one consumer's throw never starves the others */ }
    }
  }

  function refresh() {
    if (inFlight) return inFlight
    inFlight = Promise.resolve()
      .then(() => fetch())
      .then((tick) => {
        if (tick && typeof tick === 'object') {
          last = tick
          lastAt = now()
          deliver(tick)
        }
        return tick ?? null
      })
      .catch(() => null)
      .finally(() => { inFlight = null })
    return inFlight
  }

  function start() {
    if (timer !== null) return
    timer = setIntervalFn(refresh, intervalMs)
  }

  function stop() {
    if (timer === null) return
    clearIntervalFn(timer)
    timer = null
  }

  function subscribe(fn) {
    listeners.add(fn)
    if (last && now() - lastAt < intervalMs) {
      try { fn(last) } catch { /* same rule as deliver() */ }
    }
    start()
    refresh()
    return () => {
      listeners.delete(fn)
      if (listeners.size === 0) stop()
    }
  }

  return {
    subscribe,
    refresh,
    // For tests and the odd diagnostic: is the timer running, what was last seen.
    get running() { return timer !== null },
    get last() { return last },
    get size() { return listeners.size },
  }
}

// Refresh when a tab comes back to the foreground. Bound once per bus by
// App.jsx; a no-op where there is no document (tests).
export function bindVisibilityRefresh(bus, doc = globalThis.document) {
  if (!doc || typeof doc.addEventListener !== 'function') return () => {}
  const onChange = () => { if (doc.visibilityState === 'visible' && bus.size > 0) bus.refresh() }
  doc.addEventListener('visibilitychange', onChange)
  return () => doc.removeEventListener('visibilitychange', onChange)
}
